/**
 * Hook to read the user's Nostr keypair from Evolu.
 *
 * Queries the userIdentity table for the Nostr public and private keys
 * derived during wallet creation (BIP-85 index 1 -> NIP-06).
 * Returns null when no identity exists (wallet not yet created).
 *
 * Tier: All tiers (infrastructure for Nostr-based features).
 *
 * @module hooks/useNostrIdentity
 */

import { useMemo, useRef } from 'react';
import { useEvolu, useQuery } from '@evolu/react';
import { hexToBytes } from '@noble/hashes/utils';

/** A Nostr identity with public and private key. */
export interface NostrIdentity {
  /** Hex-encoded 32-byte Nostr public key. */
  readonly pubkey: string;
  /** Raw 32-byte Nostr private key. */
  readonly privkey: Uint8Array;
}

/**
 * Read the user's Nostr keypair from Evolu userIdentity table.
 *
 * Returns null if no identity row exists (wallet not created yet).
 * Memoizes the Uint8Array conversion to avoid re-creating on every render.
 * Uses useRef for createQuery to prevent subscription tracking breakage.
 *
 * @returns NostrIdentity with pubkey and privkey, or null
 */
export function useNostrIdentity(): NostrIdentity | null {
  const evolu = useEvolu();

  const queryRef = useRef(
    evolu.createQuery((db) =>
      db
        .selectFrom('userIdentity')
        .select(['nostrPubkey', 'nostrPrivkey'])
        .where('isDeleted', 'is not', 1 as never),
    ),
  );

  const rows = useQuery(queryRef.current);
  const row = rows.length > 0 ? rows[0] : null;

  const pubkey = row?.nostrPubkey != null ? String(row.nostrPubkey) : null;
  const privkeyHex = row?.nostrPrivkey != null ? String(row.nostrPrivkey) : null;

  // Memoize Uint8Array conversion to avoid re-creating on every render
  const privkey = useMemo((): Uint8Array | null => {
    if (!privkeyHex) return null;
    return hexToBytes(privkeyHex);
  }, [privkeyHex]);

  if (!pubkey || !privkey) return null;

  return { pubkey, privkey };
}
